import React, { useContext, useEffect, useState } from 'react';
import CategoryIcons from '../../components/Categories/CategoryIcons/CategoryIcons.jsx';
import Advance from '../../components/Advance/Advance.jsx';
import CategoryCard from '../../components/Category/Card/CategoryCard.jsx';
import { CategoriesContext, CategoriesCompleteContext } from '../../services/context/CategoriesContext.js';
import { EligibilityContext } from '../../services/context/EligibilityContext.js';
import { defaultAnalyticsVariables, events, pagePrefix } from '../../services/Constants/Analytics.js';
import { AnalyticsPageContext } from '../../services/context/AnalyticsPageContext.js';
import { UserDetailsContext } from '../../services/context/UserDetailsContext.js';
import { withProfiler } from '@sentry/react';
import Carousel from '../../components/Carousel/Carousel.jsx';
import { PromosContext } from '../../services/context/PromosContext.js';
import LandingPagePopUp from '../../components/LandingPagePopUp/LandingPagePopUp.jsx';
import useApis from '../../services/hooks/useApis.js';
import { BearerTokenContext } from '../../services/context/BearerTokenContext.js';
import { SubscriptionDetailsContext, UpdateSubscriptionDetailsContext } from '../../services/context/SubscriptionDetailsContext.js';
import { CampaignContext } from '../../services/context/CampaignContext.js';
import ROUTES from '../../services/Constants/GlobalRoutes.jsx';
import { useNavigate } from 'react-router-dom';
import HotDealVouchers from '../../components/HotDealVouchers/HotDealVouchers.jsx';
import Panel from '../../components/Panel/Panel.jsx';
import moment from 'moment/moment.js';
import { home } from '../../services/Constants/config.js';
import useNormalizedSubscriptionStatus from '../../services/utils/subscriptionUtils.js';
import { MoneyFormatterContext } from '../../services/context/MoneyFormatterContext.js';

/**
 * @returns {JSX.Element} HomePage component
 */
function Home() {
  const userDetails = useContext(UserDetailsContext);
  const categories = useContext(CategoriesContext);
  const promos = useContext(PromosContext);
  const eligibility = useContext(EligibilityContext);
  const analyticsName = `${pagePrefix}: landing`;
  const bearerToken = useContext(BearerTokenContext);
  const categoriesComplete = useContext(CategoriesCompleteContext);

  const subscriptionDetails = useContext(SubscriptionDetailsContext);
  const [isShowPopUp, setIsShowPopUp] = useState(false);
  const setSubscriptionDetails = useContext(UpdateSubscriptionDetailsContext);
  const campaignIDs = useContext(CampaignContext);
  const { getSubscriptionPopUp, getTotalSavings } = useApis();
  const navigateTo = useNavigate();
  const [hideHotDeals, sethideHotDeals] = useState(true);
  let campaignVouchers = null;
  const subscriptionStatus = useNormalizedSubscriptionStatus();
  const MoneyFormatter = useContext(MoneyFormatterContext);

  const categoryHeaders = [
    name => `${name} brands for you`,
    name => `More ${name?.toLowerCase()}, less money`,
    name => `All your ${name?.toLowerCase()} needs`,
  ];

  const handleShowPopUpChange = value => {
    setIsShowPopUp(value);
    if (!value) {
      sessionStorage.setItem('isShowpopupClosed', 'true');
    }
  };

  // Get total savings of a user
  const getUsersTotalSavings = async () => {
    if (bearerToken) {
      let totalSavings = 0;
      let errorMessage = null;
      try {
        const { data } = await getTotalSavings(bearerToken);
        totalSavings = data.totalSavings ?? 0;
        errorMessage = totalSavings !== 0 ? ''
          : 'Your savings will show up here once you start purchasing club vouchers.';
        localStorage.setItem('totalSavings', MoneyFormatter.format(MoneyFormatter.get(totalSavings)));
        localStorage.setItem('totalSavings-error', errorMessage);
      } catch {
        errorMessage = "We're sorry. We can't show your savings total right now.";
        localStorage.setItem('totalSavings-error', errorMessage);
      }
    }
  };

  const onClickAnalytics = () => {
    window.utag?.link({
      ...defaultAnalyticsVariables,
      event_name: [events.interaction],
      page_name: `${pagePrefix}: landing`,
      link_id: `${pagePrefix}: hot deals for you: view all`,
      visitor_addon_type_active: `${defaultAnalyticsVariables.app_name}: subscription: ${subscriptionStatus}`,
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      if (bearerToken && userDetails.vsp) {
        const response = await getSubscriptionPopUp(bearerToken);
        setSubscriptionDetails(response);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (userDetails.userId && userDetails.email && userDetails.msisdn) {
      const userInfo = {
        visitor_id_user: userDetails.userId,
        visitor_id_asset_active: userDetails.msisdn,
        visitor_id_marketing_email: userDetails.email,
      };
      window.utag?.view({
        ...defaultAnalyticsVariables,
        page_name: analyticsName,
        event_name: [events.pageView],
        ...userInfo,
      });
    }
  }, [userDetails]);

  useEffect(() => {
    const popupclosed = sessionStorage.getItem('isShowpopupClosed');

    if (!popupclosed && subscriptionDetails?.isShowPopUp && !subscriptionDetails?.isSubscribed && subscriptionDetails?.isEligibleToSubscribe) {
      setIsShowPopUp(true);
    }

    if (subscriptionDetails?.isSubscribed) {
      getUsersTotalSavings();
    }
  }, [subscriptionDetails]);

  useEffect(() => {
    if (categories && categoriesComplete) {
      const allVouchers = categories.reduce((mergedVendors, category) => [
        ...mergedVendors,
        ...category.vendors,
      ], []).reduce((mergedVouchers, nextVendor) => {
        const vouchersWithVendorInfo = nextVendor.vouchers.map(v => ({
          ...v,
          vendorName: nextVendor.name,
          vendorIconUrl: nextVendor.iconUrl,
        }));
        return [
          ...mergedVouchers,
          ...vouchersWithVendorInfo,
        ];
      }, []);
      campaignVouchers = [];
      campaignVouchers = allVouchers.filter(voucher => voucher.campaign && (campaignIDs?.length === 0
        || campaignIDs?.includes((voucher.campaign).toString())));

      campaignVouchers = campaignVouchers.filter(voucher => moment().isBetween(voucher.campaignStartDate, voucher.campaignEndDate));
      if (campaignIDs?.length > 0 && campaignVouchers?.length > 0) {
        sethideHotDeals(false);
      }
    }

    localStorage.setItem('campaignIds', campaignIDs);
  }, [
    campaignIDs,
    categories,
    categoriesComplete,
  ]);

  const loadedCategories = categories.filter(category => category.vendors.length > 0 && !category.isClubCategory);
  /** @type {JSX.Element[]} */
  const categoryCards = loadedCategories.map((category, index) => (
    <CategoryCard
      key={ category.id }
      category={ category }
      description={ categoryHeaders[index % categoryHeaders.length](category.name) }
    />
  ));

  return (
    <AnalyticsPageContext.Provider value={ analyticsName }>
      {
        promos.length > 0
          ? <Carousel items={ promos } />
          : null
      }
      <CategoryIcons />
      {
        eligibility?.tcl
        && <Advance amountAvailable={ eligibility.tcl } />
      }
      {
        userDetails?.vsp === true && !hideHotDeals
        && (
          <Panel
            panelHeaderText='Hot Deals for you'
            actionText='View all'
            onClick={
              () => {
                navigateTo(ROUTES.HOT_DEALS.get(campaignIDs));
                onClickAnalytics();
              }
            }
          >
            <HotDealVouchers
              maxSize={ 2 }
              campaignIds={ campaignIDs }
              pageLabel={ home }
            />
          </Panel>
        )
      }
      {
        categoryCards.length > 0 ? categoryCards
          : Array.from({ length: 4 }).map((_, index) => <CategoryCard key={ index } />)
      }
      {
        isShowPopUp && <LandingPagePopUp
          onShowPopupChange={ handleShowPopUpChange }
          subscriptionDetails={ subscriptionDetails }
        />
      }
    </AnalyticsPageContext.Provider>
  );
}

export default withProfiler(Home);
